import React, { useContext, useEffect, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import Chart from "./Chart";

const getTodaySaoPaulo = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 240,
  },
  customFixedHeightPaper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    height: 120,
  },
  filterRow: {
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [selectedDate, setSelectedDate] = useState(getTodaySaoPaulo());
  const [metrics, setMetrics] = useState({
    salesRevenue: 0,
    clicks: 0,
    conversationsStarted: 0,
  });
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  let userQueueIds = [];
  if (user.queues && user.queues.length > 0) {
    userQueueIds = user.queues.map((q) => q.id);
  }

  const GetTickets = (status, showAll, withUnreadMessages) => {
    const { count } = useTickets({
      status: status,
      showAll: showAll,
      withUnreadMessages: withUnreadMessages,
      queueIds: JSON.stringify(userQueueIds),
    });
    return count;
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoadingMetrics(true);
      try {
        const { data } = await api.get("/dashboard/metrics", {
          params: { date: selectedDate },
        });
        setMetrics({
          salesRevenue: data.salesRevenue || 0,
          clicks: data.clicks || 0,
          conversationsStarted: data.conversationsStarted || 0,
        });
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchMetrics();
  }, [selectedDate]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value || 0));

  return (
    <div>
      <Container maxWidth="lg" className={classes.container}>
        <div className={classes.filterRow}>
          <TextField
            type="date"
            label={i18n.t("dashboard.metrics.filterDate")}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper className={classes.customFixedHeightPaper}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.metrics.sales.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {loadingMetrics ? "…" : formatCurrency(metrics.salesRevenue)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className={classes.customFixedHeightPaper}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.metrics.clicks.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {loadingMetrics ? "…" : metrics.clicks}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className={classes.customFixedHeightPaper}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.metrics.conversations.title")}
              </Typography>
              <Typography component="h1" variant="h4">
                {loadingMetrics ? "…" : metrics.conversationsStarted}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.inAttendance.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("open", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.waiting.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("pending", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper className={classes.customFixedHeightPaper} style={{ overflow: "hidden" }}>
              <Typography component="h3" variant="h6" color="primary" paragraph>
                {i18n.t("dashboard.messages.closed.title")}
              </Typography>
              <Grid item>
                <Typography component="h1" variant="h4">
                  {GetTickets("closed", "true", "false")}
                </Typography>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.fixedHeightPaper}>
              <Chart />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Dashboard;
